<template>
    <div class="contact grid-layout">
        <div class="header">
            <div class="container">
                <div class="title"> Контакт {{ index }}</div>
                <div class="badges">
                    <span v-if="state.main" class="item badge success">Основной контакт</span>
                </div>
            </div>

            <div class="action">
                <button v-if="canDelete"
                        :disabled="disabledRemove"
                        class="iconed no-border remove"
                        @click="$emit('onRemoveContact')">
                    <IconTrashLarge class="icon" />
                </button>
            </div>
        </div>
        <div v-if="state.contact.status === 'LOCKED'" class="form-field">
            <div class="status value">Контакт неактуален</div>
        </div>
        <div class="form-field required">
            <div class="title">ФИО</div>
            <Combobox :append="appendContact"
                      :class="{ error: v.state.contact.fio.$error }"
                      :resolve="load"
                      :search="find"
                      :to-key="it => it.contact.id"
                      :to-title="it => it.contact.fio"
                      :value="contactId"
                      class="value combobox-fio"
                      infinity-scroll
                      placeholder="Введите ФИО"
                      show-append
                      show-append-first
                      @input="selectContact"
            >
                <template #option="{ item }">
                    <div class="option">
                        <div class="name">{{ item.contact.fio }}</div>
                        <div v-if="item.contact.email && item.contact.phone" class="info">
                            {{ item.contact.email }}, {{ item.contact.phone | prettyPhone }}
                        </div>
                        <div v-else-if="item.contact.email" class="email">{{ item.contact.email }}</div>
                        <div v-else-if="item.contact.phone" class="phone">{{ item.contact.phone | prettyPhone }}</div>
                    </div>
                </template>

                <template #append="{ searchText }">
                    <template v-if="!searchText">Для добавления нового контакта введите его ФИО</template>
                    <template v-else>
                        <button class="no-border iconed action">
                            <IconPlusSmall class="icon" />
                            <span>Создать новый контакт</span>
                        </button>
                    </template>
                </template>
            </Combobox>

            <ErrorMessageTooltip :filtered-rules="['required']"
                                 :value="v.state.contact.fio"
                                 bind-to=".combobox-fio input"
            />
        </div>

        <div class="form-field">
            <div class="title">Должность</div>
            <input v-model="state.contact.post"
                   :class="{ error: v.state.contact.post.$error }"
                   :disabled="!newContact"
                   class="value post"
                   placeholder="Введите должность"
                   type="text"
            />

            <ErrorMessageTooltip :value="v.state.contact.post" bind-to=".post" />
        </div>

        <div class="row">
            <div class="col col-6">
                <div class="form-field">
                    <div class="title">Телефон</div>
                    <MaskedInput v-model="state.contact.phone"
                                 :a-class="{ error : v.state.contact.phone.$error }"
                                 :disabled="!newContact"
                                 :raw-value="false"
                                 class="value masked-phone"
                                 mask="+7 (###) ###-##-##"
                                 placeholder="+7 ___ ___-__-__"
                    />

                    <ErrorMessageTooltip :value="v.state.contact.phone" bind-to=".masked-phone input" />
                </div>
            </div>

            <div class="col col-6">
                <div class="form-field required">
                    <div class="title">Email</div>
                    <input v-model="state.contact.email"
                           :class="{ error : v.state.contact.email.$error }"
                           :disabled="!newContact"
                           class="value email"
                           placeholder="Введите email"
                           type="text"
                    />

                    <ErrorMessageTooltip :value="v.state.contact.email" bind-to=".email" />
                </div>
            </div>
        </div>

        <div class="form-field">
            <div class="title">Дополнительная информация</div>
            <textarea v-model.trim="state.contact.extra"
                      :class="{ error: v.state.contact.extra.$error }"
                      :disabled="!newContact"
                      class="value extra"
                      placeholder="Укажите любую дополнительную информацию о контакте"
            />

            <ErrorMessageTooltip :value="v.state.contact.extra" bind-to=".extra" />
        </div>

        <div v-if="canSelectMain" class="form-field">
            <Radio v-model="state.main"
                   :disabled="lastContact"
                   :value="true"
                   class="value"
                   name="main"
                   @change="onSelectMain">
                Сделать этот контакт основным
            </Radio>
        </div>
    </div>
</template>

<script>
import { emptyModel } from '@/common/page_comp/Contacts/utli.js';
import { updateState } from '@/common/util/util.js';
import { extraTextRulesDefault } from '@/common/util/validations/extra.text.js';
import { debounceAsync } from '@dsee/util/async.js';
import { emailRulesDefault } from '@/common/util/validations/email.js';
import { fioRulesDefault } from '@/common/util/validations/fio.js';
import { requiredRule } from '@/common/util/validations/main.js';
import { phoneRulesDefault } from '@/common/util/validations/phone.js';
import { postRulesDefault } from '@/common/util/validations/post.js';
import { exists, find, load } from '@/restapi/contact/contact.dict.js';
import { deepCopy } from '@dsee/util/objects.js';
import { useVuelidate } from '@vuelidate/core';
import { helpers, or, requiredIf } from '@vuelidate/validators';


export default {
    props: {
        value: {
            type: Object,
            default: emptyModel(),
        },
        canDelete: {
            type: Boolean,
            default: true,
        },
        canSelectMain: {
            type: Boolean,
            default: true,
        },
        disabledRemove: {
            type: Boolean,
            default: false,
        },
        required: {
            type: Boolean,
            default: true,
        },
        lastContact: {
            type: Boolean,
            default: true,
        },
        index: {
            type: Number,
            default: 0,
        },
    },
    setup() {
        return {
            v: useVuelidate({ $autoDirty: true }),
        };
    },
    validations() {
        return {
            state: {
                contact: {
                    fio: {
                        ...fioRulesDefault(),
                        ...requiredRule(requiredIf(or(!!this.contactId, this.required))),
                    },
                    phone: {
                        ...phoneRulesDefault(!this.newContact),
                    },
                    email: {
                        ...emailRulesDefault(),
                        ...requiredRule(requiredIf(!!this.contactId)),
                        allowed: helpers.withMessage(
                            'Пользователь/контакт с указанным email уже существует',
                            helpers.withAsync(debounceAsync(async (value = '') => {
                                value = value.trim();

                                if (!value)
                                    return true;

                                if (this.original?.contact?.email === value && this.contactId !== -1)
                                    return true;

                                try {
                                    const resp = await exists({
                                        query: value,
                                        exclude: this.contactId,
                                    });

                                    return !resp;
                                } catch (e) {
                                    console.warn(e);
                                    return false;
                                }
                            }, 100)),
                        ),
                    },
                    post: {
                        ...postRulesDefault(),
                    },
                    extra: {
                        ...extraTextRulesDefault(),
                    },
                },
            },
        };
    },
    data() {
        return {
            state: emptyModel(),

            contactId: null,
            original: {},
        };
    },
    computed: {
        newContact() {
            return this.contactId === -1;
        },
    },
    created() {
        this.setInnerState(this.value);
    },
    methods: {
        find,
        load(id) {
            if (id === -1)
                return this.state;

            return load({ contact: id, type: this.state.type });
        },
        setInnerState(newState) {
            updateState(this.state, newState);

            let newContactId = null;

            if (newState)
                newContactId = newState.contact.id;

            if (newContactId !== this.contactId)
                this.original = deepCopy(newState);

            this.contactId = newContactId;
        },
        selectContact(id, obj) {
            if (!id) {
                this.setInnerState(emptyModel());

                if (this.lastContact)
                    this.onSelectMain();

                return;
            }

            this.setInnerState(obj);
        },
        appendContact(fio) {
            if (!fio) return;

            const contact = {
                ...this.state.contact,
                id: -1,
                fio,
            };
            const result = {
                type: 'CONTACT',
                contact,
            };

            this.setInnerState(result);
            this.v.$validate();
            return result;
        },
        onSelectMain() {
            if (!this.canSelectMain)
                return;

            this.state.main = true;
            this.$emit('selectMain');
        },
    },
    watch: {
        state: {
            deep: true,
            handler(value) {
                this.$emit('input', Object.assign({}, value));
            },
        },
        value: {
            deep: true,
            handler(value) {
                this.setInnerState(value);
            },
        },
    },
};
</script>

<style lang="scss" scoped>
.contact {
    .header {
        display: flex;
        align-content: center;
        margin-bottom: 16px;

        .container {
            display: flex;
            flex-grow: 2;
        }

        .title {
            font-weight: 500;
            font-size: 16px;
            line-height: 22px;
            margin-right: 8px;
        }

        .badge {
            font-size: 12px;
            font-weight: 500;
            line-height: 16px;
            letter-spacing: 0.02em;
        }

        .action .remove .icon {
            margin: 0;
        }
    }

    .combobox-fio {
        :deep(.append) {
            color: var(--input-placeholder-color);

            .action {
                color: var(--text-color);
            }
        }
    }

    .status {
        font-size: 12px;
        line-height: 16px;
        font-weight: 500;
        padding: 2px 8px;
        background-color: #F3F4F7;
        border-radius: 8px;
        width: fit-content;
    }

    .extra {
        min-height: 76px;
    }
}
</style>
